import { useCallback, useEffect, useRef, useState } from 'react'

import type { AgentCommand } from '../../lib/api/admin.ts'
import { AthenaError } from '../../lib/api/client.ts'
import { describeError } from '../../lib/api/messages.ts'
import { machines } from '../../lib/api/resources.ts'
import type { MachineResponse } from '../../lib/api/types.ts'
import {
  RELEASE_TTL_DEFAULT_SECONDS,
  RELEASE_TTL_MAX_SECONDS,
  RELEASE_TTL_MIN_SECONDS,
} from '../../lib/api/types.ts'
import { useAuth } from '../../lib/auth/AuthContext.tsx'
import { isAwaitingAgent, isConfirmed, openCommandIdOf } from '../../lib/commands/semantics.ts'
import { UNINSTALL_KIND, uninstallTriggerLabel, uninstallTriggerOf } from '../../lib/commands/uninstall.ts'
import { useCommandTracking } from '../../lib/commands/useCommandTracking.ts'
import { formatDayClock } from '../../lib/format.ts'
import { useAction } from '../../lib/useAction.ts'
import { useResource } from '../../lib/useResource.ts'
import { Badge } from '../primitives/Badge.tsx'
import { Button } from '../primitives/Button.tsx'
import { Dialog } from '../primitives/Dialog.tsx'
import { SelectField } from '../primitives/Field.tsx'
import { Pagination } from '../primitives/Pagination.tsx'
import { EmptyState, ErrorState, LoadingRow, Notice } from '../primitives/States.tsx'
import { TABLE, TD, TH, THEAD, TR } from '../primitives/table.ts'
import { useToast } from '../primitives/Toast.tsx'
import { CommandStateBadge, ReleaseStatus } from '../commands/CommandState.tsx'

/** Rótulo da máquina: apelido, senão fingerprint. */
export function machineLabel(machine: MachineResponse): string {
  return machine.displayName ?? machine.fingerprint
}

/**
 * Selo de isolamento de rede. Só aparece quando a listagem CONSULTOU o estado:
 * `isolation` nulo/ausente é "não sei", e não vira "conectada".
 */
export function IsolationBadge({ machine }: { machine: MachineResponse }) {
  const isolation = machine.isolation
  if (isolation == null || !isolation.is_isolated) return null
  return (
    <Badge tone="crit" dot>
      {isolation.isolated_since === null
        ? 'isolada'
        : `isolada desde ${formatDayClock(isolation.isolated_since)}`}
    </Badge>
  )
}

export const TTL_OPTIONS = [
  { value: '300', label: '5 minutos' },
  { value: String(RELEASE_TTL_DEFAULT_SECONDS), label: '15 minutos (padrão)' },
  { value: '1800', label: '30 minutos' },
  { value: String(RELEASE_TTL_MAX_SECONDS), label: '60 minutos' },
].filter((o) => Number(o.value) >= RELEASE_TTL_MIN_SECONDS && Number(o.value) <= RELEASE_TTL_MAX_SECONDS)

type Phase = { kind: 'form' } | { kind: 'tracking'; id: string; initial: AgentCommand | null }

/**
 * Desbloqueio de rede de um host isolado (`release-host` → `restore_host`).
 *
 * Duas fases no mesmo diálogo: o PEDIDO (confirmação + justificativa) e o
 * ACOMPANHAMENTO. O 202 só quer dizer "na fila": a tela diz "liberada" apenas
 * quando o agente confirma (`executed`/`skipped`), e então relê a máquina.
 *
 * A chave de idempotência nasce com o diálogo — um clique em "Desbloquear" —, e
 * não a cada envio: um retry depois de uma falha de rede devolve o MESMO comando
 * em vez de criar outro. Reemitir (prazo vencido, falha) gera chave nova.
 */
export function ReleaseHostDialog({
  machine,
  track = null,
  onClose,
  onReleased,
}: {
  machine: MachineResponse
  /** Abre direto no acompanhamento deste comando (vindo do histórico). */
  track?: AgentCommand | null
  onClose: () => void
  /** A máquina voltou à rede (confirmado pelo agente). Quem chama recarrega a lista. */
  onReleased: () => void
}) {
  const [phase, setPhase] = useState<Phase>(
    track === null ? { kind: 'form' } : { kind: 'tracking', id: track.id, initial: track },
  )
  const [round, setRound] = useState(0)

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title="Desbloquear rede do host"
      description={
        <>
          <span className="font-medium text-fg">{machineLabel(machine)}</span>
          {machine.isolation?.isolated_since != null && (
            <> · isolada desde {formatDayClock(machine.isolation.isolated_since)}</>
          )}
        </>
      }
      wide
    >
      {phase.kind === 'form' ? (
        <ReleaseForm
          key={round}
          machine={machine}
          onAccepted={(command) => setPhase({ kind: 'tracking', id: command.id, initial: command })}
          onAlreadyOpen={(id) => setPhase({ kind: 'tracking', id, initial: null })}
          onCancel={onClose}
        />
      ) : (
        <ReleaseTracking
          key={phase.id}
          machine={machine}
          commandId={phase.id}
          initial={phase.initial}
          onReleased={onReleased}
          onReissue={() => {
            setRound((n) => n + 1)
            setPhase({ kind: 'form' })
          }}
          onClose={onClose}
        />
      )}
    </Dialog>
  )
}

function ReleaseForm({
  machine,
  onAccepted,
  onAlreadyOpen,
  onCancel,
}: {
  machine: MachineResponse
  onAccepted: (command: AgentCommand) => void
  onAlreadyOpen: (id: string) => void
  onCancel: () => void
}) {
  const { isAdmin } = useAuth()
  const action = useAction()
  const [reason, setReason] = useState('')
  const [ttl, setTtl] = useState(String(RELEASE_TTL_DEFAULT_SECONDS))
  const [confirmed, setConfirmed] = useState(false)
  // Uma chave por CLIQUE em "Desbloquear" (este formulário), estável entre retries.
  const idempotencyKey = useRef(crypto.randomUUID()).current

  // O detalhe traz o último comando: um desbloqueio ainda aberto é para
  // acompanhar, não para duplicar.
  const detailFetcher = useCallback((signal: AbortSignal) => machines.get(machine.id, signal), [machine.id])
  const detail = useResource(detailFetcher)
  const last = detail.data?.isolation?.last_command ?? null
  const openRelease = last !== null && last.kind === 'restore_host' && isAwaitingAgent(last.state) ? last : null
  const failedRelease = last !== null && last.kind === 'restore_host' && last.state === 'failed' ? last : null

  const reasonMissing = reason.trim() === ''
  const disabled = !isAdmin || reasonMissing || !confirmed

  const submit = (): void => {
    if (disabled) return
    action.run(async () => {
      try {
        const command = await machines.releaseHost(machine.id, {
          reason: reason.trim(),
          idempotencyKey,
          expiresInSeconds: Number(ttl),
        })
        onAccepted(command)
      } catch (cause) {
        // Já há um desbloqueio aberto: leve o operador até ele, não a um beco.
        if (cause instanceof AthenaError && cause.code === 'agent_command_already_open') {
          const id = openCommandIdOf(cause.problem.detail)
          if (id !== null) {
            onAlreadyOpen(id)
            return
          }
        }
        throw cause
      }
    })
  }

  return (
    <div className="space-y-3">
      <Notice tone="warn">Devolve à rede uma máquina possivelmente comprometida.</Notice>

      {!isAdmin && <Notice tone="crit">O desbloqueio exige o papel de administrador.</Notice>}

      {openRelease !== null && (
        <Notice tone="neutral">
          Já existe um desbloqueio em andamento ({openRelease.state === 'delivered' ? 'entregue, sem confirmação' : 'na fila'}).{' '}
          <button type="button" className="underline" onClick={() => onAlreadyOpen(openRelease.id)}>
            Acompanhar esse comando
          </button>
        </Notice>
      )}

      {failedRelease !== null && (
        <Notice tone="crit">
          O último desbloqueio falhou
          {(failedRelease.failure_detail ?? failedRelease.result?.detail) != null &&
            `: ${failedRelease.failure_detail ?? failedRelease.result?.detail}`}
          . Verifique o agente antes de tentar de novo.
        </Notice>
      )}

      <label className="flex flex-col gap-1">
        <span className="text-micro font-medium uppercase tracking-label text-fg-secondary">
          Justificativa <span className="text-fg-muted">*</span>
        </span>
        <textarea
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          maxLength={512}
          rows={3}
          required
          autoFocus
          className="w-full border border-line-strong bg-inset px-2 py-1 text-base text-fg placeholder:text-fg-muted"
          placeholder="ex.: incidente encerrado, host reimageado — ticket INC-4471"
        />
      </label>

      <SelectField
        label="Validade do pedido"
        value={ttl}
        onValueChange={setTtl}
        options={TTL_OPTIONS}
        className="max-w-60"
      />

      <label className="flex items-start gap-2 text-sm text-fg-secondary">
        <input
          type="checkbox"
          checked={confirmed}
          onChange={(event) => setConfirmed(event.target.checked)}
          className="mt-0.5 size-3.5 shrink-0"
          style={{ accentColor: 'var(--c-accent)' }}
        />
        <span>
          Confirmo que <span className="font-medium text-fg">{machineLabel(machine)}</span> pode voltar à rede.
        </span>
      </label>

      <div className="flex items-center justify-end gap-2 border-t border-line-subtle pt-3">
        {action.error !== null && (
          <p role="alert" className="mr-auto text-sm leading-snug text-crit">
            {describeError(action.error)}
          </p>
        )}
        <Button variant="ghost" onClick={onCancel}>
          Cancelar
        </Button>
        <Button variant="danger" busy={action.busy} disabled={disabled} onClick={submit}>
          Desbloquear
        </Button>
      </div>
    </div>
  )
}

function ReleaseTracking({
  machine,
  commandId,
  initial,
  onReleased,
  onReissue,
  onClose,
}: {
  machine: MachineResponse
  commandId: string
  initial: AgentCommand | null
  onReleased: () => void
  onReissue: () => void
  onClose: () => void
}) {
  const toast = useToast()
  const { command, error, refresh } = useCommandTracking(commandId, initial)
  // Depois da confirmação, a máquina é relida: `is_isolated` deve ter virado false.
  const [verified, setVerified] = useState<MachineResponse | null>(null)
  const [verifyError, setVerifyError] = useState<unknown>(null)
  const announced = useRef(false)
  // Só anuncia "voltou à rede" se a confirmação aconteceu DIANTE do operador —
  // abrir um desbloqueio antigo do histórico não é notícia.
  const sawAwaiting = useRef(false)

  const state = command?.state ?? null
  if (state !== null && isAwaitingAgent(state)) sawAwaiting.current = true
  const confirmed = state !== null && isConfirmed(state)

  useEffect(() => {
    if (!confirmed) return
    const controller = new AbortController()
    machines
      .get(machine.id, controller.signal)
      .then((fresh) => {
        setVerified(fresh)
        if (!announced.current && sawAwaiting.current) {
          announced.current = true
          toast.success(`${machineLabel(machine)} voltou à rede.`)
          onReleased()
        }
      })
      .catch((cause: unknown) => {
        if (!controller.signal.aborted) setVerifyError(cause)
      })
    return () => controller.abort()
    // Só a confirmação dispara a releitura; `announced` evita anunciar duas vezes.
  }, [confirmed, machine.id])

  const canReissue = state === 'expired' || state === 'failed' || state === 'cancelled'

  return (
    <div className="space-y-3">
      {command === null ? (
        error !== null ? (
          <ErrorState error={error} onRetry={refresh} />
        ) : (
          <LoadingRow label="Lendo o comando…" />
        )
      ) : (
        <>
          <ReleaseStatus command={command} />
          {error !== null && (
            <Notice tone="warn">
              Não foi possível atualizar o estado: {describeError(error)}{' '}
              <button type="button" className="underline" onClick={refresh}>
                Tentar de novo
              </button>
            </Notice>
          )}
          {confirmed && verified?.isolation != null && verified.isolation.is_isolated && (
            <Notice tone="warn">
              O agente confirmou, mas a máquina ainda consta como isolada. Confira os alertas da máquina.
            </Notice>
          )}
          {confirmed && verifyError !== null && (
            <Notice tone="neutral">Não foi possível reler a máquina: {describeError(verifyError)}</Notice>
          )}
        </>
      )}

      <div className="flex items-center justify-end gap-2 border-t border-line-subtle pt-3">
        <span className="mr-auto font-mono text-micro text-fg-muted">comando {commandId}</span>
        {canReissue && (
          <Button variant="danger" onClick={onReissue}>
            Reemitir desbloqueio
          </Button>
        )}
        <Button variant={confirmed ? 'primary' : 'ghost'} onClick={onClose}>
          Fechar
        </Button>
      </div>
    </div>
  )
}

/** Histórico paginado de comandos da máquina (`GET /machines/{id}/commands`). */
export function MachineCommandsDialog({
  machine,
  onClose,
  onTrack,
  onTrackUninstall,
}: {
  machine: MachineResponse
  onClose: () => void
  onTrack: (command: AgentCommand) => void
  onTrackUninstall: (command: AgentCommand) => void
}) {
  const [page, setPage] = useState(1)
  const fetcher = useCallback(
    (signal: AbortSignal) => machines.commands(machine.id, { page, pageSize: 20 }, signal),
    [machine.id, page],
  )
  const { data, error, loading, reload } = useResource(fetcher)

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()} title="Comandos da máquina" description={machineLabel(machine)} wide>
      {error !== null ? (
        <ErrorState error={error} onRetry={reload} />
      ) : data === null ? (
        <LoadingRow />
      ) : data.items.length === 0 ? (
        <EmptyState title="Nenhum comando emitido para esta máquina." />
      ) : (
        <>
          <table className={TABLE}>
            <thead className={THEAD}>
              <tr>
                <th scope="col" className={TH}>
                  Pedido
                </th>
                <th scope="col" className={TH}>
                  Comando
                </th>
                <th scope="col" className={TH}>
                  Situação
                </th>
                <th scope="col" className={TH}>
                  Justificativa
                </th>
                <th scope="col" className={`${TH} text-right`}>
                  <span className="sr-only">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((command) => (
                <tr key={command.id} className={TR}>
                  <td className={`${TD} tnum whitespace-nowrap`}>{formatDayClock(command.requested_at)}</td>
                  <td className={TD}>
                    <span className="font-mono">{command.kind}</span>
                    {command.kind === UNINSTALL_KIND && (
                      <span className="block text-micro text-fg-muted">
                        {uninstallTriggerLabel(uninstallTriggerOf(command))}
                      </span>
                    )}
                  </td>
                  <td className={TD}>
                    <CommandStateBadge state={command.state} />
                  </td>
                  <td className={`${TD} max-w-0`}>
                    <span className="block truncate text-fg-secondary" title={command.reason ?? undefined}>
                      {command.reason ?? '—'}
                    </span>
                  </td>
                  <td className={`${TD} text-right`}>
                    {command.kind === 'restore_host' && (
                      <Button size="sm" variant="ghost" onClick={() => onTrack(command)}>
                        Detalhe
                      </Button>
                    )}
                    {command.kind === UNINSTALL_KIND && (
                      <Button size="sm" variant="ghost" onClick={() => onTrackUninstall(command)}>
                        Detalhe
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            page={data.page}
            pageSize={data.pageSize}
            totalCount={data.totalCount}
            totalPages={data.totalPages}
            busy={loading}
            onPageChange={setPage}
          />
        </>
      )}
    </Dialog>
  )
}
